import { Grid } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import useMediaQuery from '@mui/material/useMediaQuery';
import dayjs from 'dayjs';
import timezone from 'dayjs/plugin/timezone';
import utc from 'dayjs/plugin/utc';
import { useEffect, useRef, useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import HttpServices from 'src/services/httpService';
import { integrateModuleURL, postsModuleURL } from 'src/services/urlService';
import { CommonHelper } from 'src/utils/commonHelper';
import Spinner from 'src/utils/Loader/spinner';
import { useReAuth } from 'src/utils/reAuth/ReAuthContext';
import { useToaster } from 'src/utils/toaster/toasterContext';
import CommonReAuthenticateComponent from '../integration/commonReAuthenticate';
import MobileNewPostComponent from './mobile-new-post.component';
import PostContentComponent from './post-content.component';
import PostFooter from './post-footer.component';
import PostTabComponent from './post-tab.component';
import { PostUtil } from './post-util';

// Extend dayjs with the plugins
dayjs.extend(utc);
dayjs.extend(timezone);

const NewPostComponent = ({ isShowOfferBanner }) => {
  let currentbrandTime = CommonHelper.getBrandTimeZoneTime();
  const PostOption = {
    PostNow: { Name: 'postnow', Label: 'Post Now' },
    SchedulePost: { Name: 'schedulepost', Label: 'Scheduled Post' },
    ApprovalPost: { Name: 'approval', Label: 'Send for Approval' },
  };

  const NewPostStruct = {
    media_url: '',
    media_data: [],
    description: '',
    scheduled_time: currentbrandTime,
    is_file_upload: false,
    connected_profile_ids: [],
    scheduled_event_requests_data: {
      facebook_requests_data: [],
      instagram_requests_data: [],
      twitter_requests_data: [],
      threads_requests_data: [],
      youtube_requests_data: [],
      gbusiness_requests_data: [],
      linkedin_requests_data: [],
      bluesky_requests_data: [],
      tiktok_requests_data: [],
      pinterest_requests_data: [],
      snapchat_requests_data: [],
    },
    comment_description: '',
    has_comment: false,
  };

  const location = useLocation();
  const { showToast } = useToaster();
  const { t: translate } = useTranslation();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const hasApproverPermission = CommonHelper.CheckForPermissionAvailableForUser(CommonHelper.PermissionList.APPROVER);
  const { checkAndTriggerReAuth } = useReAuth();
  //State Variables
  const [loader, setLoader] = useState(true);
  const [configuredSocialMedia, setConfiguredSocialMedia] = useState([]);
  const [selectedSocialMedias, setSelectedSocialMedias] = useState([]);
  const [scheduleDateTime, setScheduleDateTime] = useState(currentbrandTime);
  const [scheduleId, setScheduleId] = useState(null);
  const [isSubmitting, setPostSubmitting] = useState(false);
  const [isSubmittingDraft, setDraftSubmitting] = useState(false);
  const [whenToPost, setWhenToPost] = useState(
    hasApproverPermission ? PostOption.PostNow.Name : PostOption.ApprovalPost.Name
  );
  const [newPostForm, setNewPostForm] = useState(NewPostStruct);
  const [tabValue, setTabValue] = useState(1);
  const [hashTags, setHashTags] = useState('');
  const [selectedPopOverAccount, setSelectedPopOverAccount] = useState();
  const [reAuthenticateOpen, setReAuthenticateOpen] = useState(false);

  //Ref Variables
  const facebookConditionRef = useRef(null);
  const instagramConditionRef = useRef(null);
  const twitterConditionRef = useRef(null);
  const youtubeConditionRef = useRef(null);
  const linkedInConditionRef = useRef(null);
  const tiktokConditionRef = useRef(null);
  const threadsConditionRef = useRef(null);
  const googleMyBusinessConditionRef = useRef(null);
  const pinterestConditionRef = useRef(null);
  const blueskyConditionRef = useRef(null);
  const snapchatConditionRef = useRef(null);
  const previewRef = useRef(null);

  const setStateToInitialStage = () => {
    setSelectedSocialMedias([]);
    setScheduleDateTime(currentbrandTime);
    setWhenToPost(hasApproverPermission ? PostOption.PostNow.Name : PostOption.ApprovalPost.Name);
    setNewPostForm(NewPostStruct);
    setHashTags('');

    facebookConditionRef?.current?.handleRequestData([]);
    instagramConditionRef?.current?.handleRequestData([]);
    twitterConditionRef?.current?.handleRequestData([]);
    threadsConditionRef?.current?.handleRequestData([]);
    youtubeConditionRef?.current?.handleRequestData([]);
    tiktokConditionRef?.current?.handleRequestData([]);
    linkedInConditionRef?.current?.handleRequestData([]);
    googleMyBusinessConditionRef?.current?.handleRequestData([]);
    pinterestConditionRef?.current?.handleRequestData([]);
    blueskyConditionRef?.current?.handleRequestData([]);
    snapchatConditionRef?.current?.handleRequestData([]);
    previewRef?.current?.handlePreviewData(JSON.parse(JSON.stringify(NewPostStruct)));
  };

  useEffect(() => {
    getConnectedNetworks();
  }, []);

  const getConnectedNetworks = () => {
    setLoader(true);
    let getProps = {
      url: integrateModuleURL.getConnectedNetworks,
      successCallback: connectedNetworksSuccessCallback,
      failureCallback,
    };
    HttpServices.Get(getProps);
  };
  const connectedNetworksSuccessCallback = (data, message) => {
    setLoader(false);
    message && showToast(message, 'e');
    const filterConnectedNetworks = data?.filter((a) => a.is_enabled);
    const filterActiveConnectedNetworks = data?.filter((a) => a.is_enabled && a.is_active);
    setConfiguredSocialMedia(filterConnectedNetworks);
    //TODO: Need to check the plan and need to select
    if (location.state?.data && !location.state?.data?.is_org_level_post) {
      const scheduledTime = CommonHelper.getBrandTimeZoneTime(location.state.data.scheduled_time);
      const currentTime = currentbrandTime;
      setWhenToPost(scheduledTime > currentTime ? PostOption.SchedulePost.Name : PostOption.PostNow.Name);
      setScheduleId(location.state.data?.schedule_id);
      setScheduleDateTime(scheduledTime > currentTime ? scheduledTime : currentTime);
      if (location.state.data.connected_profile_ids && location.state.data.connected_profile_ids.length > 0) {
        let selectedSocialMediaAccounts = filterActiveConnectedNetworks.filter((a) =>
          location.state.data.connected_profile_ids.includes(a.id)
        );

        let formData = { ...location.state?.data };
        let facebook_requests_data = formData.scheduled_event_requests_data.facebook_requests_data?.filter((a) =>
          selectedSocialMediaAccounts.find((i) => i.id == a.connected_profile_details.connected_profile_id)
        );
        let instagram_requests_data = formData.scheduled_event_requests_data.instagram_requests_data?.filter((a) =>
          selectedSocialMediaAccounts.find((i) => i.id == a.connected_profile_details.connected_profile_id)
        );
        let twitter_requests_data = formData.scheduled_event_requests_data.twitter_requests_data?.filter((a) =>
          selectedSocialMediaAccounts.find((i) => i.id == a.connected_profile_details.connected_profile_id)
        );
        let threads_requests_data = formData.scheduled_event_requests_data.threads_requests_data?.filter((a) =>
          selectedSocialMediaAccounts.find((i) => i.id == a.connected_profile_details.connected_profile_id)
        );
        let youtube_requests_data = formData.scheduled_event_requests_data.youtube_requests_data?.filter((a) =>
          selectedSocialMediaAccounts.find((i) => i.id == a.connected_profile_details.connected_profile_id)
        );
        let gbusiness_requests_data = formData.scheduled_event_requests_data.gbusiness_requests_data?.filter((a) =>
          selectedSocialMediaAccounts.find((i) => i.id == a.connected_profile_details.connected_profile_id)
        );
        let linkedin_requests_data = formData.scheduled_event_requests_data.linkedin_requests_data?.filter((a) =>
          selectedSocialMediaAccounts.find((i) => i.id == a.connected_profile_details.connected_profile_id)
        );
        let tiktok_requests_data = formData.scheduled_event_requests_data.tiktok_requests_data?.filter((a) =>
          selectedSocialMediaAccounts.find((i) => i.id == a.connected_profile_details.connected_profile_id)
        );
        let pinterest_requests_data = formData.scheduled_event_requests_data.pinterest_requests_data?.filter((a) =>
          selectedSocialMediaAccounts.find((i) => i.id == a.connected_profile_details.connected_profile_id)
        );
        let bluesky_requests_data = formData.scheduled_event_requests_data.bluesky_requests_data?.filter((a) =>
          selectedSocialMediaAccounts.find((i) => i.id == a.connected_profile_details.connected_profile_id)
        );
        let snapchat_requests_data = formData.scheduled_event_requests_data.snapchat_requests_data?.filter((a) =>
          selectedSocialMediaAccounts.find((i) => i.id == a.connected_profile_details.connected_profile_id)
        );
        formData.scheduled_event_requests_data.facebook_requests_data = facebook_requests_data;
        formData.scheduled_event_requests_data.instagram_requests_data = instagram_requests_data;
        formData.scheduled_event_requests_data.twitter_requests_data = twitter_requests_data;
        formData.scheduled_event_requests_data.threads_requests_data = threads_requests_data;
        formData.scheduled_event_requests_data.youtube_requests_data = youtube_requests_data;
        formData.scheduled_event_requests_data.gbusiness_requests_data = gbusiness_requests_data;
        formData.scheduled_event_requests_data.linkedin_requests_data = linkedin_requests_data;
        formData.scheduled_event_requests_data.tiktok_requests_data = tiktok_requests_data;
        formData.scheduled_event_requests_data.pinterest_requests_data = pinterest_requests_data;
        formData.scheduled_event_requests_data.bluesky_requests_data = bluesky_requests_data;
        formData.scheduled_event_requests_data.snapchat_requests_data = snapchat_requests_data;
        setNewPostForm(formData);
        setSelectedSocialMedias(selectedSocialMediaAccounts);
      }
    } else if (location.state?.media_data && !location.state?.data?.is_org_level_post) {
      setSelectedSocialMedias(filterActiveConnectedNetworks);
      processConnectedNetworksData(filterActiveConnectedNetworks);
      let mediaData = location.state.media_data;
      handleMediaFiles(mediaData.media_data);
    } else {
      setSelectedSocialMedias(filterActiveConnectedNetworks);
      processConnectedNetworksData(filterActiveConnectedNetworks);
    }
    checkAndTriggerReAuth(data);
  };
  const processConnectedNetworksData = (connectedNetworks) => {
    let formData = { ...NewPostStruct };
    connectedNetworks.map((network) => {
      formData.connected_profile_ids = formData.connected_profile_ids.concat(network.id);
      switch (network.service_name.toUpperCase()) {
        case PostUtil.ServiceName.FACEBOOK:
          let facebookData = {
            connected_profile_id: network.id,
            media_url: '',
            description: '',
            link: '',
            thumbnail: '',
            comment_description: formData.comment_description,
            has_comment: false,
            post_type: PostUtil.PostType.POST,
            is_automation_trigger: false,
            automation_trigger: {
              trigger_type: '',
              trigger_values: [],
              any_trigger_value: false,
              dm_message: '',
              comment_reply: '',
              is_dm_template: false,
              dm_template: {
                title: '',
                subtitle: '',
                image_url: '',
                default_action: { type: 'web_url', url: '' },
                buttons: [],
              },
            },
            connected_profile_details: {
              connected_profile_id: network.id,
              connected_profile_name: network.profile_name,
              connected_profile_image: network.profile_image,
              social_media: network.service_name,
              webhook_profile_id: network.webhook_profile_id,
            },
          };
          formData.scheduled_event_requests_data.facebook_requests_data.push(facebookData);
          break;
        case PostUtil.ServiceName.INSTAGRAM:
          let instagramData = {
            connected_profile_id: network.id,
            media_url: '',
            description: '',
            cover_url: '',
            post_type: PostUtil.PostType.POST,
            comment_description: formData.comment_description,
            post_on_my_wall: true,
            has_comment: false,
            is_automation_trigger: false,
            automation_trigger: {
              trigger_type: '',
              trigger_values: [],
              any_trigger_value: false,
              dm_message: '',
              comment_reply: '',
              is_dm_template: false,
              dm_template: {
                title: '',
                subtitle: '',
                image_url: '',
                default_action: { type: 'web_url', url: '' },
                buttons: [],
              },
            },
            connected_profile_details: {
              connected_profile_id: network.id,
              connected_profile_name: network.profile_name,
              connected_profile_image: network.profile_image,
              social_media: network.service_name,
              webhook_profile_id: network.webhook_profile_id,
            },
          };
          formData.scheduled_event_requests_data.instagram_requests_data.push(instagramData);
          break;
        case PostUtil.ServiceName.TWITTER:
          let twitterData = {
            connected_profile_id: network.id,
            media_url: '',
            description: '',
            cover_url: '',
            post_type: PostUtil.PostType.POST,
            connected_profile_details: {
              connected_profile_id: network.id,
              connected_profile_name: network.profile_name,
              connected_profile_image: network.profile_image,
              social_media: network.service_name,
            },
          };
          formData.scheduled_event_requests_data.twitter_requests_data.push(twitterData);
          break;
        case PostUtil.ServiceName.THREADS:
          let threadsData = {
            connected_profile_id: network.id,
            media_url: '',
            description: '',
            post_type: PostUtil.PostType.POST,
            connected_profile_details: {
              connected_profile_id: network.id,
              connected_profile_name: network.profile_name,
              connected_profile_image: network.profile_image,
              social_media: network.service_name,
            },
          };
          formData.scheduled_event_requests_data.threads_requests_data.push(threadsData);
          break;
        case PostUtil.ServiceName.YOUTUBE:
          let youtubeData = {
            connected_profile_id: network.id,
            media_url: '',
            description: '',
            thumbnail: '',
            post_type: PostUtil.PostType.VIDEO,
            comment_description: formData.comment_description,
            has_comment: false,
            privacy: PostUtil.YoutubePrivacyLevel.PUBLIC,
            connected_profile_details: {
              connected_profile_id: network.id,
              connected_profile_name: network.profile_name,
              connected_profile_image: network.profile_image,
              social_media: network.service_name,
              has_custom_thumbnail_permission: network.has_custom_thumbnail_permission,
            },
          };
          formData.scheduled_event_requests_data.youtube_requests_data.push(youtubeData);
          break;
        case PostUtil.ServiceName.LINKEDIN:
          let linkedData = {
            connected_profile_id: network.id,
            media_url: '',
            alert_text: '',
            description: '',
            post_type: PostUtil.PostType.POST,
            comment_description: formData.comment_description,
            has_comment: false,
            connected_profile_details: {
              connected_profile_id: network.id,
              connected_profile_name: network.profile_name,
              connected_profile_image: network.profile_image,
              social_media: network.service_name,
            },
          };
          formData.scheduled_event_requests_data.linkedin_requests_data.push(linkedData);
          break;
        case PostUtil.ServiceName.BLUESKY:
          let blueskyData = {
            connected_profile_id: network.id,
            media_url: '',
            description: '',
            post_type: PostUtil.PostType.POST,
            comment_description: formData.comment_description,
            has_comment: false,
            connected_profile_details: {
              connected_profile_id: network.id,
              connected_profile_name: network.profile_name,
              connected_profile_image: network.profile_image,
              social_media: network.service_name,
            },
          };
          formData.scheduled_event_requests_data.bluesky_requests_data.push(blueskyData);
          break;
        case PostUtil.ServiceName.TIKTOK:
          let tiktokData = {
            connected_profile_id: network.id,
            media_url: '',
            description: '',
            title: '',
            post_type: PostUtil.PostType.DIRECT_POST,
            privacy: PostUtil.TikTokPrivacyLevel.PUBLIC_TO_EVERYONE,
            allow_user_to_comment: true,
            allow_user_to_duet: true,
            allow_user_to_switch: true,
            disclosure_content: false,
            your_brand: false,
            branded_content: false,
            connected_profile_details: {
              connected_profile_id: network.id,
              connected_profile_name: network.profile_name,
              connected_profile_image: network.profile_image,
              social_media: network.service_name,
            },
          };
          formData.scheduled_event_requests_data.tiktok_requests_data.push(tiktokData);
          break;
        case PostUtil.ServiceName.GBUSINESS:
          let gBusinessData = {
            connected_profile_id: network.id,
            media_url: '',
            description: '',
            post_type: PostUtil.PostType.STANDARD,
            button_type: 'None',
            connected_profile_details: {
              connected_profile_id: network.id,
              connected_profile_name: network.profile_name,
              connected_profile_image: network.profile_image,
              has_call_now_permission: network.has_call_now_permission,
              social_media: network.service_name,
            },
            start_date: currentbrandTime,
            end_time: currentbrandTime,
            start_time: currentbrandTime,
            end_date: currentbrandTime.add(7, 'days'),
          };
          formData.scheduled_event_requests_data.gbusiness_requests_data.push(gBusinessData);
          break;
        case PostUtil.ServiceName.PINTEREST:
          let pinterestData = {
            connected_profile_id: network.id,
            media_url: '',
            description: '',
            title: '',
            board_id: '',
            board_section_id: '',
            cover_image_url: '',
            post_type: PostUtil.PostType.POST,
            connected_profile_details: {
              connected_profile_id: network.id,
              connected_profile_name: network.profile_name,
              connected_profile_image: network.profile_image,
              social_media: network.service_name,
            },
          };
          formData.scheduled_event_requests_data.pinterest_requests_data.push(pinterestData);
          break;
        case PostUtil.ServiceName.SNAPCHAT:
          let snapchatData = {
            connected_profile_id: network.id,
            media_url: '',
            media_data: [],
            description: '',
            title: '',
            save_to_profile: false,
            post_type: PostUtil.PostType.PUBLIC_STORY, // Default
            connected_profile_details: {
              connected_profile_id: network.id,
              connected_profile_name: network.profile_name,
              connected_profile_image: network.profile_image,
              social_media: network.service_name,
            },
          };
          formData.scheduled_event_requests_data.snapchat_requests_data.push(snapchatData);
          break;
        default:
          break;
      }
    });
    setNewPostForm({ ...formData });
  };
  const failureCallback = (message) => {
    setLoader(false);
    setDraftSubmitting(false);
    setPostSubmitting(false);
    message && showToast(message, 'e');
  };
  const handleNetworks = (socialMedias) => {
    setSelectedSocialMedias([...socialMedias]);
    if (socialMedias.length == 0) {
      handleDeleteAllNetworks();
      return;
    }
    let selectedSocialNetworks = [...selectedSocialMedias];
    let deletedNetwork = null;
    let addedNetwork = null;
    socialMedias.map((media) => {
      let index = selectedSocialNetworks.findIndex((a) => a.id === media.id);
      if (index >= 0) {
        selectedSocialNetworks.splice(index, 1);
      } else {
        addedNetwork = media;
      }
    });
    deletedNetwork = selectedSocialNetworks?.length > 0 ? selectedSocialNetworks[0] : null;

    if (addedNetwork) {
      handleAddNewNetwork(addedNetwork);
    } else {
      handleDeleteNetwork(deletedNetwork);
    }
  };
  const handleAddNewNetwork = (addedNetwork) => {
    let newFormData = { ...newPostForm };
    newFormData.connected_profile_ids.push(addedNetwork.id);

    const commonData = {
      connected_profile_id: addedNetwork.id,
      media_url: newFormData.media_url,
      media_data: newFormData.media_data,
      description: newFormData.description,
      comment_description: newFormData.comment_description,
      post_type: whenToPost,
    };

    switch (addedNetwork.service_name.toUpperCase()) {
      case PostUtil.ServiceName.FACEBOOK:
        newFormData.scheduled_event_requests_data.facebook_requests_data.push({
          ...commonData,
          post_type: PostUtil.PostType.POST,
          comment_description: commonData.comment_description,
          has_comment: false,
          link: '',
          thumbnail: '',
          is_automation_trigger: false,
          automation_trigger: {
            trigger_type: '',
            trigger_values: [],
            any_trigger_value: false,
            dm_message: '',
            comment_reply: '',
            is_dm_template: false,
            dm_template: {
              title: '',
              subtitle: '',
              image_url: '',
              default_action: { type: 'web_url', url: '' },
              buttons: [],
            },
          },
          connected_profile_details: {
            connected_profile_id: addedNetwork.id,
            connected_profile_name: addedNetwork.profile_name,
            connected_profile_image: addedNetwork.profile_image,
            social_media: addedNetwork.service_name,
            webhook_profile_id: addedNetwork.webhook_profile_id,
          },
        });
        if (facebookConditionRef && facebookConditionRef.current) {
          facebookConditionRef.current.handleRequestData(
            newFormData.scheduled_event_requests_data.facebook_requests_data
          );
        }
        break;
      case PostUtil.ServiceName.INSTAGRAM:
        newFormData.scheduled_event_requests_data.instagram_requests_data.push({
          ...commonData,
          post_type: PostUtil.PostType.POST,
          comment_description: commonData.comment_description,
          cover_url: '',
          has_comment: false,
          post_on_my_wall: true,
          is_automation_trigger: false,
          automation_trigger: {
            trigger_type: '',
            trigger_values: [],
            any_trigger_value: false,
            dm_message: '',
            comment_reply: '',
            is_dm_template: false,
            dm_template: {
              title: '',
              subtitle: '',
              image_url: '',
              default_action: { type: 'web_url', url: '' },
              buttons: [],
            },
          },
          connected_profile_details: {
            connected_profile_id: addedNetwork.id,
            connected_profile_name: addedNetwork.profile_name,
            connected_profile_image: addedNetwork.profile_image,
            social_media: addedNetwork.service_name,
            webhook_profile_id: addedNetwork.webhook_profile_id,
          },
        });
        if (instagramConditionRef && instagramConditionRef.current) {
          instagramConditionRef.current.handleRequestData(
            newFormData.scheduled_event_requests_data.instagram_requests_data
          );
        }
        break;
      case PostUtil.ServiceName.TWITTER:
        newFormData.scheduled_event_requests_data.twitter_requests_data.push({
          ...commonData,
          post_type: PostUtil.PostType.POST,
          connected_profile_details: {
            connected_profile_id: addedNetwork.id,
            connected_profile_name: addedNetwork.profile_name,
            connected_profile_image: addedNetwork.profile_image,
            social_media: addedNetwork.service_name,
          },
        });
        if (twitterConditionRef && twitterConditionRef.current) {
          twitterConditionRef.current.handleRequestData(
            newFormData.scheduled_event_requests_data.twitter_requests_data
          );
        }
        break;
      case PostUtil.ServiceName.THREADS:
        newFormData.scheduled_event_requests_data.threads_requests_data.push({
          ...commonData,
          post_type: PostUtil.PostType.POST,
          connected_profile_details: {
            connected_profile_id: addedNetwork.id,
            connected_profile_name: addedNetwork.profile_name,
            connected_profile_image: addedNetwork.profile_image,
            social_media: addedNetwork.service_name,
          },
        });
        if (threadsConditionRef && threadsConditionRef.current) {
          threadsConditionRef.current.handleRequestData(
            newFormData.scheduled_event_requests_data.threads_requests_data
          );
        }
        break;
      case PostUtil.ServiceName.YOUTUBE:
        newFormData.scheduled_event_requests_data.youtube_requests_data.push({
          ...commonData,
          post_type: PostUtil.PostType.VIDEO,
          comment_description: commonData.comment_description,
          has_comment: false,
          privacy: PostUtil.YoutubePrivacyLevel.PUBLIC,
          thumbnail: '',
          connected_profile_details: {
            connected_profile_id: addedNetwork.id,
            connected_profile_name: addedNetwork.profile_name,
            connected_profile_image: addedNetwork.profile_image,
            has_custom_thumbnail_permission: addedNetwork.has_custom_thumbnail_permission,
            social_media: addedNetwork.service_name,
          },
        });
        if (youtubeConditionRef && youtubeConditionRef.current) {
          youtubeConditionRef.current.handleRequestData(
            newFormData.scheduled_event_requests_data.youtube_requests_data
          );
        }
        break;
      case PostUtil.ServiceName.LINKEDIN:
        newFormData.scheduled_event_requests_data.linkedin_requests_data.push({
          ...commonData,
          alert_text: '',
          post_type: PostUtil.PostType.POST,
          comment_description: commonData.comment_description,
          has_comment: false,
          connected_profile_details: {
            connected_profile_id: addedNetwork.id,
            connected_profile_name: addedNetwork.profile_name,
            connected_profile_image: addedNetwork.profile_image,
            social_media: addedNetwork.service_name,
          },
        });
        if (linkedInConditionRef && linkedInConditionRef.current) {
          linkedInConditionRef.current.handleRequestData(
            newFormData.scheduled_event_requests_data.linkedin_requests_data
          );
        }
        break;
      case PostUtil.ServiceName.BLUESKY:
        newFormData.scheduled_event_requests_data.bluesky_requests_data.push({
          ...commonData,
          post_type: PostUtil.PostType.POST,
          comment_description: commonData.comment_description,
          has_comment: false,
          connected_profile_details: {
            connected_profile_id: addedNetwork.id,
            connected_profile_name: addedNetwork.profile_name,
            connected_profile_image: addedNetwork.profile_image,
            social_media: addedNetwork.service_name,
          },
        });
        if (blueskyConditionRef && blueskyConditionRef.current) {
          blueskyConditionRef.current.handleRequestData(
            newFormData.scheduled_event_requests_data.bluesky_requests_data
          );
        }
        break;
      case PostUtil.ServiceName.TIKTOK:
        newFormData.scheduled_event_requests_data.tiktok_requests_data.push({
          ...commonData,
          title: '',
          post_type: PostUtil.PostType.DIRECT_POST,
          privacy: PostUtil.TikTokPrivacyLevel.PUBLIC_TO_EVERYONE,
          allow_user_to_comment: true,
          allow_user_to_duet: true,
          allow_user_to_switch: true,
          disclosure_content: false,
          your_brand: false,
          branded_content: false,
          connected_profile_details: {
            connected_profile_id: addedNetwork.id,
            connected_profile_name: addedNetwork.profile_name,
            connected_profile_image: addedNetwork.profile_image,
            social_media: addedNetwork.service_name,
          },
        });
        if (tiktokConditionRef && tiktokConditionRef.current) {
          tiktokConditionRef.current.handleRequestData(newFormData.scheduled_event_requests_data.tiktok_requests_data);
        }
        break;
      case PostUtil.ServiceName.GBUSINESS:
        newFormData.scheduled_event_requests_data.gbusiness_requests_data.push({
          ...commonData,
          button_type: 'None',
          post_type: PostUtil.PostType.STANDARD,
          start_date: currentbrandTime,
          end_time: currentbrandTime,
          start_time: currentbrandTime,
          end_date: currentbrandTime.add(7, 'days'),
          connected_profile_details: {
            connected_profile_id: addedNetwork.id,
            connected_profile_name: addedNetwork.profile_name,
            connected_profile_image: addedNetwork.profile_image,
            has_call_now_permission: addedNetwork.has_call_now_permission,
            social_media: addedNetwork.service_name,
          },
        });
        if (googleMyBusinessConditionRef && googleMyBusinessConditionRef.current) {
          googleMyBusinessConditionRef.current.handleRequestData(
            newFormData.scheduled_event_requests_data.gbusiness_requests_data
          );
        }
        break;
      case PostUtil.ServiceName.PINTEREST:
        newFormData.scheduled_event_requests_data.pinterest_requests_data.push({
          ...commonData,
          title: '',
          board_id: '',
          board_section_id: '',
          link: '',
          cover_image_url: '',
          post_type: PostUtil.PostType.POST,
          connected_profile_details: {
            connected_profile_id: addedNetwork.id,
            connected_profile_name: addedNetwork.profile_name,
            connected_profile_image: addedNetwork.profile_image,
            social_media: addedNetwork.service_name,
          },
        });
        if (pinterestConditionRef && pinterestConditionRef.current) {
          pinterestConditionRef.current.handleRequestData(
            newFormData.scheduled_event_requests_data.pinterest_requests_data
          );
        }
        break;
      case PostUtil.ServiceName.SNAPCHAT:
        newFormData.scheduled_event_requests_data.snapchat_requests_data.push({
          ...commonData,
          title: '',
          save_to_profile: false,
          post_type: PostUtil.PostType.PUBLIC_STORY,
          connected_profile_details: {
            connected_profile_id: addedNetwork.id,
            connected_profile_name: addedNetwork.profile_name,
            connected_profile_image: addedNetwork.profile_image,
            social_media: addedNetwork.service_name,
          },
        });

        if (snapchatConditionRef && snapchatConditionRef.current) {
          snapchatConditionRef.current.handleRequestData(
            newFormData.scheduled_event_requests_data.snapchat_requests_data
          );
        }
        break;
      default:
        break;
    }
    //Preview Ref
    if (previewRef && previewRef.current) {
      let fData = JSON.parse(JSON.stringify(newFormData));
      previewRef.current.handlePreviewData(fData);
    }
    setNewPostForm({ ...newFormData });
  };

  const handleDeleteAllNetworks = () => {
    let newFormData = { ...newPostForm };
    newFormData.connected_profile_ids = [];
    const platforms = [
      'facebook_requests_data',
      'instagram_requests_data',
      'twitter_requests_data',
      'threads_requests_data',
      'youtube_requests_data',
      'linkedin_requests_data',
      'tiktok_requests_data',
      'gbusiness_requests_data',
      'pinterest_requests_data',
      'bluesky_requests_data',
      'snapchat_requests_data',
    ];
    platforms.forEach((platform) => {
      newFormData.scheduled_event_requests_data[platform] = [];
    });
    facebookConditionRef?.current?.handleRequestData([]);
    instagramConditionRef?.current?.handleRequestData([]);
    twitterConditionRef?.current?.handleRequestData([]);
    threadsConditionRef?.current?.handleRequestData([]);
    youtubeConditionRef?.current?.handleRequestData([]);
    linkedInConditionRef?.current?.handleRequestData([]);
    tiktokConditionRef?.current?.handleRequestData([]);
    googleMyBusinessConditionRef?.current?.handleRequestData([]);
    pinterestConditionRef?.current?.handleRequestData([]);
    blueskyConditionRef?.current?.handleRequestData([]);
    snapchatConditionRef?.current?.handleRequestData([]);
    previewRef?.current?.handlePreviewData(JSON.parse(JSON.stringify(newFormData)));

    setNewPostForm({ ...newFormData });
  };
  const handleDeleteNetwork = (deletedNetwork) => {
    let newFormData = { ...newPostForm };
    newFormData.connected_profile_ids = newFormData.connected_profile_ids?.filter((id) => id !== deletedNetwork?.id);
    switch (deletedNetwork?.service_name.toUpperCase()) {
      case PostUtil.ServiceName.FACEBOOK:
        newFormData.scheduled_event_requests_data.facebook_requests_data =
          newFormData.scheduled_event_requests_data.facebook_requests_data.filter(
            (data) => data.connected_profile_id !== deletedNetwork.id
          );
        if (facebookConditionRef && facebookConditionRef.current) {
          facebookConditionRef.current.handleRequestData(
            newFormData.scheduled_event_requests_data.facebook_requests_data
          );
        }
        break;
      case PostUtil.ServiceName.INSTAGRAM:
        newFormData.scheduled_event_requests_data.instagram_requests_data =
          newFormData.scheduled_event_requests_data.instagram_requests_data.filter(
            (data) => data.connected_profile_id !== deletedNetwork.id
          );
        if (instagramConditionRef && instagramConditionRef.current) {
          instagramConditionRef.current.handleRequestData(
            newFormData.scheduled_event_requests_data.instagram_requests_data
          );
        }
        break;
      case PostUtil.ServiceName.TWITTER:
        newFormData.scheduled_event_requests_data.twitter_requests_data =
          newFormData.scheduled_event_requests_data.twitter_requests_data.filter(
            (data) => data.connected_profile_id !== deletedNetwork.id
          );
        if (twitterConditionRef && twitterConditionRef.current) {
          twitterConditionRef.current.handleRequestData(
            newFormData.scheduled_event_requests_data.twitter_requests_data
          );
        }
        break;
      case PostUtil.ServiceName.THREADS:
        newFormData.scheduled_event_requests_data.threads_requests_data =
          newFormData.scheduled_event_requests_data.threads_requests_data.filter(
            (data) => data.connected_profile_id !== deletedNetwork.id
          );
        if (threadsConditionRef && threadsConditionRef.current) {
          threadsConditionRef.current.handleRequestData(
            newFormData.scheduled_event_requests_data.threads_requests_data
          );
        }
        break;
      case PostUtil.ServiceName.YOUTUBE:
        newFormData.scheduled_event_requests_data.youtube_requests_data =
          newFormData.scheduled_event_requests_data.youtube_requests_data.filter(
            (data) => data.connected_profile_id !== deletedNetwork.id
          );
        if (youtubeConditionRef && youtubeConditionRef.current) {
          youtubeConditionRef.current.handleRequestData(
            newFormData.scheduled_event_requests_data.youtube_requests_data
          );
        }
        break;
      case PostUtil.ServiceName.LINKEDIN:
        newFormData.scheduled_event_requests_data.linkedin_requests_data =
          newFormData.scheduled_event_requests_data.linkedin_requests_data.filter(
            (data) => data.connected_profile_id !== deletedNetwork.id
          );
        if (linkedInConditionRef && linkedInConditionRef.current) {
          linkedInConditionRef.current.handleRequestData(
            newFormData.scheduled_event_requests_data.linkedin_requests_data
          );
        }
        break;
      case PostUtil.ServiceName.BLUESKY:
        newFormData.scheduled_event_requests_data.bluesky_requests_data =
          newFormData.scheduled_event_requests_data.bluesky_requests_data.filter(
            (data) => data.connected_profile_id !== deletedNetwork.id
          );
        if (blueskyConditionRef && blueskyConditionRef.current) {
          blueskyConditionRef.current.handleRequestData(
            newFormData.scheduled_event_requests_data.bluesky_requests_data
          );
        }
        break;
      case PostUtil.ServiceName.SNAPCHAT:
        newFormData.scheduled_event_requests_data.snapchat_requests_data =
          newFormData.scheduled_event_requests_data.snapchat_requests_data.filter(
            (data) => data.connected_profile_id !== deletedNetwork.id
          );
        if (snapchatConditionRef && snapchatConditionRef.current) {
          snapchatConditionRef.current.handleRequestData(
            newFormData.scheduled_event_requests_data.snapchat_requests_data
          );
        }
        break;
      case PostUtil.ServiceName.TIKTOK:
        newFormData.scheduled_event_requests_data.tiktok_requests_data =
          newFormData.scheduled_event_requests_data.tiktok_requests_data.filter(
            (data) => data.connected_profile_id !== deletedNetwork.id
          );
        if (tiktokConditionRef && tiktokConditionRef.current) {
          tiktokConditionRef.current.handleRequestData(newFormData.scheduled_event_requests_data.tiktok_requests_data);
        }
        break;
      case PostUtil.ServiceName.GBUSINESS:
        newFormData.scheduled_event_requests_data.gbusiness_requests_data =
          newFormData.scheduled_event_requests_data.gbusiness_requests_data.filter(
            (data) => data.connected_profile_id !== deletedNetwork.id
          );
        if (googleMyBusinessConditionRef && googleMyBusinessConditionRef.current) {
          googleMyBusinessConditionRef.current.handleRequestData(
            newFormData.scheduled_event_requests_data.gbusiness_requests_data
          );
        }
        break;
      case PostUtil.ServiceName.PINTEREST:
        newFormData.scheduled_event_requests_data.pinterest_requests_data =
          newFormData.scheduled_event_requests_data.pinterest_requests_data.filter(
            (data) => data.connected_profile_id !== deletedNetwork.id
          );
        if (pinterestConditionRef && pinterestConditionRef.current) {
          pinterestConditionRef.current.handleRequestData(
            newFormData.scheduled_event_requests_data.pinterest_requests_data
          );
        }
        break;
      default:
        break;
    }
    //Preview Ref
    if (previewRef && previewRef.current) {
      let fData = JSON.parse(JSON.stringify(newFormData));
      previewRef.current.handlePreviewData(fData);
    }
    setNewPostForm({ ...newFormData });
  };

  const handleAddHashTags = (content) => {
    setHashTags(content);
  };

  const handleAddAllDescription = (description) => {
    let newFormData = { ...newPostForm };
    newFormData.description = description;
    let facebookRequestData = newFormData.scheduled_event_requests_data.facebook_requests_data;
    let instagramRequestData = newFormData.scheduled_event_requests_data.instagram_requests_data;
    let youtubeRequestData = newFormData.scheduled_event_requests_data.youtube_requests_data;
    let twitterRequestData = newFormData.scheduled_event_requests_data.twitter_requests_data;
    let threadsRequestData = newFormData.scheduled_event_requests_data.threads_requests_data;
    let linkedinRequestData = newFormData.scheduled_event_requests_data.linkedin_requests_data;
    let tiktokRequestsData = newFormData.scheduled_event_requests_data.tiktok_requests_data;
    let gbusinessRequestData = newFormData.scheduled_event_requests_data.gbusiness_requests_data;
    let pinterestRequestData = newFormData.scheduled_event_requests_data.pinterest_requests_data;
    let blueskyRequestData = newFormData.scheduled_event_requests_data.bluesky_requests_data;
    let snapchatRequestData = newFormData.scheduled_event_requests_data.snapchat_requests_data;
    if (facebookRequestData?.length > 0) {
      facebookRequestData.map((request) => {
        request.description = newFormData.description;
      });
      if (facebookConditionRef && facebookConditionRef.current) {
        facebookConditionRef.current.handleRequestData(facebookRequestData);
      }
    }
    if (instagramRequestData?.length > 0) {
      instagramRequestData.map((request) => {
        request.description = newFormData.description;
      });
      if (instagramConditionRef && instagramConditionRef.current) {
        instagramConditionRef.current.handleRequestData(instagramRequestData);
      }
    }
    if (youtubeRequestData?.length > 0) {
      youtubeRequestData.map((request) => {
        request.description = newFormData.description;
      });
      if (youtubeConditionRef && youtubeConditionRef.current) {
        youtubeConditionRef.current.handleRequestData(youtubeRequestData);
      }
    }
    if (twitterRequestData?.length > 0) {
      twitterRequestData.map((request) => {
        request.description = newFormData.description;
      });
      if (twitterConditionRef && twitterConditionRef.current) {
        twitterConditionRef.current.handleRequestData(twitterRequestData);
      }
    }
    if (threadsRequestData?.length > 0) {
      threadsRequestData.map((request) => {
        request.description = newFormData.description;
      });
      if (threadsConditionRef && threadsConditionRef.current) {
        threadsConditionRef.current.handleRequestData(threadsRequestData);
      }
    }
    if (linkedinRequestData?.length > 0) {
      linkedinRequestData.map((request) => {
        request.description = newFormData.description;
      });
      if (linkedInConditionRef && linkedInConditionRef.current) {
        linkedInConditionRef.current.handleRequestData(linkedinRequestData);
      }
    }
    if (pinterestRequestData?.length > 0) {
      pinterestRequestData.map((request) => {
        request.description = newFormData.description;
      });
      if (pinterestConditionRef && pinterestConditionRef.current) {
        pinterestConditionRef.current.handleRequestData(pinterestRequestData);
      }
    }
    if (tiktokRequestsData?.length > 0) {
      tiktokRequestsData.map((request) => {
        request.description = newFormData.description;
      });
      if (tiktokConditionRef && tiktokConditionRef.current) {
        tiktokConditionRef.current.handleRequestData(tiktokRequestsData);
      }
    }
    if (gbusinessRequestData?.length > 0) {
      gbusinessRequestData.map((request) => {
        request.description = newFormData.description;
      });
      if (googleMyBusinessConditionRef && googleMyBusinessConditionRef.current) {
        googleMyBusinessConditionRef.current.handleRequestData(gbusinessRequestData);
      }
    }
    if (blueskyRequestData?.length > 0) {
      blueskyRequestData.map((request) => {
        request.description = newFormData.description;
      });
      if (blueskyConditionRef && blueskyConditionRef.current) {
        blueskyConditionRef.current.handleRequestData(blueskyRequestData);
      }
    }
    if (snapchatRequestData?.length > 0) {
      snapchatRequestData.map((request) => {
        request.description = newFormData.description;
      });
      if (snapchatConditionRef && snapchatConditionRef.current) {
        snapchatConditionRef.current.handleRequestData(snapchatRequestData);
      }
    }
    //Preview Ref
    if (previewRef && previewRef.current) {
      let fData = JSON.parse(JSON.stringify(newFormData));
      previewRef.current.handlePreviewData(fData);
    }
    setNewPostForm(newFormData);
  };

  const handleAddAllCommentDescription = (commentDescription) => {
    let newFormData = { ...newPostForm };
    newFormData.comment_description = commentDescription;
    let facebookRequestData = newFormData.scheduled_event_requests_data.facebook_requests_data;
    let instagramRequestData = newFormData.scheduled_event_requests_data.instagram_requests_data;
    let youtubeRequestData = newFormData.scheduled_event_requests_data.youtube_requests_data;
    let twitterRequestData = newFormData.scheduled_event_requests_data.twitter_requests_data;
    let threadsRequestData = newFormData.scheduled_event_requests_data.threads_requests_data;
    let linkedinRequestData = newFormData.scheduled_event_requests_data.linkedin_requests_data;
    let gbusinessRequestData = newFormData.scheduled_event_requests_data.gbusiness_requests_data;
    let blueskyRequestData = newFormData.scheduled_event_requests_data.bluesky_requests_data;
    let snapchatRequestData = newFormData.scheduled_event_requests_data.snapchat_requests_data;
    if (facebookRequestData?.length > 0) {
      facebookRequestData.map((request) => {
        request.comment_description = newFormData.comment_description;
        request.has_comment = request.comment_description != '' ? true : false;
      });
      if (facebookConditionRef && facebookConditionRef.current) {
        facebookConditionRef.current.handleRequestData(facebookRequestData);
      }
    }
    if (instagramRequestData?.length > 0) {
      instagramRequestData.map((request) => {
        request.comment_description = newFormData.comment_description;
        request.has_comment = request.comment_description != '' ? true : false;
      });
      if (instagramConditionRef && instagramConditionRef.current) {
        instagramConditionRef.current.handleRequestData(instagramRequestData);
      }
    }
    if (youtubeRequestData?.length > 0) {
      youtubeRequestData.map((request) => {
        request.comment_description = newFormData.comment_description;
        request.has_comment = request.comment_description != '' ? true : false;
      });
      if (youtubeConditionRef && youtubeConditionRef.current) {
        youtubeConditionRef.current.handleRequestData(youtubeRequestData);
      }
    }
    if (twitterRequestData?.length > 0) {
      twitterRequestData.map((request) => {
        request.comment_description = newFormData.comment_description;
      });
      if (twitterConditionRef && twitterConditionRef.current) {
        twitterConditionRef.current.handleRequestData(twitterRequestData);
      }
    }
    if (threadsRequestData?.length > 0) {
      threadsRequestData.map((request) => {
        request.comment_description = newFormData.comment_description;
      });
      if (threadsConditionRef && threadsConditionRef.current) {
        threadsConditionRef.current.handleRequestData(threadsRequestData);
      }
    }
    if (linkedinRequestData?.length > 0) {
      linkedinRequestData.map((request) => {
        request.comment_description = newFormData.comment_description;
        request.has_comment = request.comment_description != '' ? true : false;
      });
      if (linkedInConditionRef && linkedInConditionRef.current) {
        linkedInConditionRef.current.handleRequestData(linkedinRequestData);
      }
    }
    if (blueskyRequestData?.length > 0) {
      blueskyRequestData.map((request) => {
        request.comment_description = newFormData.comment_description;
        request.has_comment = request.comment_description != '' ? true : false;
      });
      if (blueskyConditionRef && blueskyConditionRef.current) {
        blueskyConditionRef.current.handleRequestData(blueskyRequestData);
      }
    }
    if (gbusinessRequestData?.length > 0) {
      gbusinessRequestData.map((request) => {
        request.comment_description = newFormData.comment_description;
      });
      if (googleMyBusinessConditionRef && googleMyBusinessConditionRef.current) {
        googleMyBusinessConditionRef.current.handleRequestData(gbusinessRequestData);
      }
    }
    if (snapchatRequestData?.length > 0) {
      snapchatRequestData.map((request) => {
        request.comment_description = newFormData.comment_description;
      });
      if (snapchatConditionRef && snapchatConditionRef.current) {
        snapchatConditionRef.current.handleRequestData(snapchatRequestData);
      }
    }
    //Preview Ref
    if (previewRef && previewRef.current) {
      let fData = JSON.parse(JSON.stringify(newFormData));
      previewRef.current.handlePreviewData(fData);
    }
    setNewPostForm(newFormData);
  };

  const handleMediaFiles = (mediaFiles) => {
    let newFormData = { ...newPostForm };
    newFormData.media_url = mediaFiles.map((a) => a.media_url).join();
    newFormData.media_data = mediaFiles;

    let {
      facebook_requests_data: facebookRequestData,
      instagram_requests_data: instagramRequestData,
      youtube_requests_data: youtubeRequestData,
      twitter_requests_data: twitterRequestData,
      threads_requests_data: threadsRequestData,
      linkedin_requests_data: linkedinRequestData,
      bluesky_requests_data: blueskyRequestData,
      tiktok_requests_data: tiktokRequestsData,
      gbusiness_requests_data: gbusinessRequestData,
      pinterest_requests_data: pinterestRequestData,
      snapchat_requests_data: snapchatRequestData,
    } = newFormData.scheduled_event_requests_data;

    const updateMediaData = (requestData, ref) => {
      if (requestData?.length > 0) {
        requestData.forEach((c) => {
          c.media_url = newFormData.media_url;
          c.media_data = mediaFiles;
        });
        if (ref && ref.current) {
          ref.current.handleRequestData(requestData);
        }
      }
    };

    updateMediaData(facebookRequestData, facebookConditionRef);
    updateMediaData(instagramRequestData, instagramConditionRef);
    updateMediaData(youtubeRequestData, youtubeConditionRef);
    updateMediaData(twitterRequestData, twitterConditionRef);
    updateMediaData(threadsRequestData, threadsConditionRef);
    updateMediaData(pinterestRequestData, pinterestConditionRef);
    updateMediaData(tiktokRequestsData, tiktokConditionRef);
    updateMediaData(gbusinessRequestData, googleMyBusinessConditionRef);
    updateMediaData(blueskyRequestData, blueskyConditionRef);
    updateMediaData(snapchatRequestData, snapchatConditionRef);

    // Special case for LinkedIn: Remove removed media, keep existing, add new ones
    if (linkedinRequestData?.length > 0) {
      linkedinRequestData.forEach((c) => {
        let existingMedia = c.media_data || [];

        // Remove media that is no longer in the selection
        let updatedMedia = existingMedia.filter((m) => mediaFiles.some((newM) => newM.media_url === m.media_url));

        // Append only new media that isn't already in the existing data
        mediaFiles.forEach((newM) => {
          if (!updatedMedia.some((m) => m.media_url === newM.media_url)) {
            updatedMedia.push(newM);
          }
        });

        c.media_data = updatedMedia;
        c.media_url = updatedMedia.map((a) => a.media_url).join();
      });

      if (linkedInConditionRef && linkedInConditionRef.current) {
        linkedInConditionRef.current.handleRequestData(linkedinRequestData);
      }
    }

    // Update preview
    if (previewRef && previewRef.current) {
      let fData = JSON.parse(JSON.stringify(newFormData));
      previewRef.current.handlePreviewData(fData);
    }

    setNewPostForm({ ...newFormData });
  };

  const handleCloseReauthenticate = () => {
    setReAuthenticateOpen(false);
    setSelectedPopOverAccount(null);
  };
  const handleProfileClick = (profile) => {
    setSelectedPopOverAccount(profile);
    setReAuthenticateOpen(true);
  };

  const updateRequestData = (data, serviceName) => {
    let newFormData = { ...newPostForm };
    switch (serviceName) {
      case PostUtil.ServiceName.FACEBOOK:
        newFormData.scheduled_event_requests_data.facebook_requests_data = [...data];
        break;
      case PostUtil.ServiceName.INSTAGRAM:
        newFormData.scheduled_event_requests_data.instagram_requests_data = [...data];
        break;
      case PostUtil.ServiceName.TWITTER:
        newFormData.scheduled_event_requests_data.twitter_requests_data = [...data];
        break;
      case PostUtil.ServiceName.THREADS:
        newFormData.scheduled_event_requests_data.threads_requests_data = [...data];
        break;
      case PostUtil.ServiceName.LINKEDIN:
        newFormData.scheduled_event_requests_data.linkedin_requests_data = [...data];
        break;
      case PostUtil.ServiceName.BLUESKY:
        newFormData.scheduled_event_requests_data.bluesky_requests_data = [...data];
        break;
      case PostUtil.ServiceName.YOUTUBE:
        newFormData.scheduled_event_requests_data.youtube_requests_data = [...data];
        break;
      case PostUtil.ServiceName.GBUSINESS:
        newFormData.scheduled_event_requests_data.gbusiness_requests_data = [...data];
        break;
      case PostUtil.ServiceName.PINTEREST:
        newFormData.scheduled_event_requests_data.pinterest_requests_data = [...data];
        break;
      case PostUtil.ServiceName.TIKTOK:
        newFormData.scheduled_event_requests_data.tiktok_requests_data = [...data];
        break;
      case PostUtil.ServiceName.SNAPCHAT:
        newFormData.scheduled_event_requests_data.snapchat_requests_data = [...data];
        break;
      default:
        break;
    }
    setNewPostForm({ ...newFormData });
  };

  const saveDraftSuccessCallback = (data, message) => {
    message && showToast(message, 's');
    // setStateToInitialStage();
    setDraftSubmitting(false);
  };
  const handleSaveDraft = () => {
    setDraftSubmitting(true);
    let bodyprops = getFormDataProps();
    let postProps = {
      url: postsModuleURL.saveDraft,
      body: bodyprops,
      successCallback: saveDraftSuccessCallback,
      failureCallback,
    };
    HttpServices.Post(postProps);
  };

  const getFormDataProps = () => {
    // Always use the current time in the specified timezone
    let postDateTime = CommonHelper.getBrandTimeZoneTime();
    let isScheduledPost = false;

    // Use the scheduled time only if scheduling or approval post
    if (whenToPost === PostOption.SchedulePost.Name || whenToPost === PostOption.ApprovalPost.Name) {
      postDateTime = CommonHelper.getBrandTimeZoneTime(scheduleDateTime);
      isScheduledPost = true;
    }

    const mediaUrls = newPostForm.media_data.map((file) => file.media_url);

    // Normalize the current time for comparison
    const now = CommonHelper.getBrandTimeZoneTime();
    const normalizeDateTime = (date) => date.set('second', 0).set('millisecond', 0);

    const normalizedPostDateTime = normalizeDateTime(postDateTime);
    const normalizedNow = normalizeDateTime(now);

    // If permissions are missing and the time matches "now," adjust by adding 5 minutes
    const adjustedPostDateTime =
      whenToPost === PostOption.PostNow.Name
        ? now // Use the exact current time for "Post Now"
        : !hasApproverPermission && normalizedPostDateTime.isSame(normalizedNow)
        ? normalizedPostDateTime.add(5, 'minute')
        : normalizedPostDateTime;

    // Form body to be sent to the backend
    const formbody = {
      scheduled_event_requests_data: newPostForm.scheduled_event_requests_data,
      scheduled_time: adjustedPostDateTime.toDate(), // Convert to native Date for backend submission
      description: newPostForm.description,
      comment_description: newPostForm.comment_description,
      media_url: mediaUrls,
      is_file_upload: true,
      is_scheduled_post: isScheduledPost,
      schedule_id: scheduleId ? scheduleId.toString() : undefined,
      is_approval: whenToPost === PostOption.ApprovalPost.Name,
    };

    return scheduleId
      ? {
          schedule_id: scheduleId,
          new_post_datas: formbody,
        }
      : formbody;
  };

  const handleScheduleDate = (date) => {
    if (date) {
      const convertedDate = CommonHelper.getBrandTimeZoneTime(date);
      setScheduleDateTime(convertedDate);
    }
  };
  const handlePostTypeChange = (event) => {
    let isChecked = event.target.checked;
    let postOption = isChecked ? PostOption.SchedulePost.Name : PostOption.PostNow.Name;
    setWhenToPost(postOption);
  };
  const handleSelectPostTypeChange = (value) => {
    setWhenToPost(value);
  };
  const newPostSuccessCallback = (data, message) => {
    CommonHelper.EnsureConversionFactorFlag(CommonHelper.ConversionFlagList.FIRST_POST_PUBLISHED);
    showToast(message, 's');
    setLoader(false);
    setPostSubmitting(false);
    handleDeleteAllNetworks();
    setStateToInitialStage();
  };
  const handleFormsubmit = async () => {
    if (selectedSocialMedias && selectedSocialMedias.length > 0) {
      // Temporarily switch to the conditions tab to validate
      setTabValue(1);
      await new Promise((resolve) => setTimeout(resolve, 100));
      let isValidForm = checkFormIsValid();
      if (isValidForm) {
        setPostSubmitting(true);
        let formBody = getFormDataProps();
        let postProps = {
          url: scheduleId ? postsModuleURL.updateScheduledPost : postsModuleURL.uploadPosts,
          body: formBody,
          successCallback: newPostSuccessCallback,
          failureCallback,
        };
        HttpServices.Post(postProps);
      } else {
        showToast(translate('new_post.fix_all_validations', 'Please fix all the validations to post'), 'e');
      }
    } else {
      showToast(translate('new_post.fix_all_validations', 'Please fix all the validations to post'), 'e');
    }
  };

  const checkFormIsValid = () => {
    let errorCount = 0;
    const { scheduled_event_requests_data } = newPostForm;
    const validateServiceData = (serviceData) => {
      serviceData.forEach((data) => {
        if (data.errors && data.errors.length > 0) {
          errorCount += data.errors.length;
        }
      });
    };
    if (
      scheduled_event_requests_data.facebook_requests_data.length > 0 &&
      facebookConditionRef &&
      facebookConditionRef.current
    ) {
      facebookConditionRef.current.checkForErrors();
      validateServiceData(scheduled_event_requests_data.facebook_requests_data);
    }
    if (
      scheduled_event_requests_data.instagram_requests_data.length > 0 &&
      instagramConditionRef &&
      instagramConditionRef.current
    ) {
      instagramConditionRef.current.checkForErrors();
      validateServiceData(scheduled_event_requests_data.instagram_requests_data);
    }
    if (
      scheduled_event_requests_data.twitter_requests_data.length > 0 &&
      twitterConditionRef &&
      twitterConditionRef.current
    ) {
      twitterConditionRef.current.checkForErrors();
      validateServiceData(scheduled_event_requests_data.twitter_requests_data);
    }
    if (
      scheduled_event_requests_data.threads_requests_data.length > 0 &&
      threadsConditionRef &&
      threadsConditionRef.current
    ) {
      threadsConditionRef.current.checkForErrors();
      validateServiceData(scheduled_event_requests_data.threads_requests_data);
    }
    if (
      scheduled_event_requests_data.youtube_requests_data.length > 0 &&
      youtubeConditionRef &&
      youtubeConditionRef.current
    ) {
      youtubeConditionRef.current.checkForErrors();
      validateServiceData(scheduled_event_requests_data.youtube_requests_data);
    }
    if (
      scheduled_event_requests_data.linkedin_requests_data.length > 0 &&
      linkedInConditionRef &&
      linkedInConditionRef.current
    ) {
      linkedInConditionRef.current.checkForErrors();
      validateServiceData(scheduled_event_requests_data.linkedin_requests_data);
    }
    if (
      scheduled_event_requests_data.bluesky_requests_data.length > 0 &&
      blueskyConditionRef &&
      blueskyConditionRef.current
    ) {
      blueskyConditionRef.current.checkForErrors();
      validateServiceData(scheduled_event_requests_data.bluesky_requests_data);
    }
    if (
      scheduled_event_requests_data.snapchat_requests_data.length > 0 &&
      snapchatConditionRef &&
      snapchatConditionRef.current
    ) {
      snapchatConditionRef.current.checkForErrors();
      validateServiceData(scheduled_event_requests_data.snapchat_requests_data);
    }
    if (
      scheduled_event_requests_data.gbusiness_requests_data.length > 0 &&
      googleMyBusinessConditionRef &&
      googleMyBusinessConditionRef.current
    ) {
      googleMyBusinessConditionRef.current.checkForErrors();
      validateServiceData(scheduled_event_requests_data.gbusiness_requests_data);
    }
    if (
      scheduled_event_requests_data.pinterest_requests_data.length > 0 &&
      pinterestConditionRef &&
      pinterestConditionRef.current
    ) {
      pinterestConditionRef.current.checkForErrors();
      validateServiceData(scheduled_event_requests_data.pinterest_requests_data);
    }
    if (
      scheduled_event_requests_data.tiktok_requests_data.length > 0 &&
      tiktokConditionRef &&
      tiktokConditionRef.current
    ) {
      tiktokConditionRef.current.checkForErrors();
      validateServiceData(scheduled_event_requests_data.tiktok_requests_data);
    }
    // if(scheduled_event_requests_data.tiktok_requests_data.length>0){
    //   facebookConditionRef.current.checkForErrors();
    //   validateServiceData(scheduled_event_requests_data.tiktok_requests_data);
    // }
    return errorCount === 0;
  };
  return (
    <>
      <Helmet>
        <title> Create New Post </title>
      </Helmet>
      {loader ? (
        <Spinner />
      ) : isMobile ? (
        <MobileNewPostComponent
          configuredSocialMedia={configuredSocialMedia}
          selectedSocialMedias={selectedSocialMedias}
          handleNetworks={handleNetworks}
          newPostForm={newPostForm}
          handleAddAllDescription={handleAddAllDescription}
          handleAddAllCommentDescription={handleAddAllCommentDescription}
          handleMediaFiles={handleMediaFiles}
          tabValue={tabValue}
          setTabValue={setTabValue}
          updateRequestData={updateRequestData}
          googleMyBusinessConditionRef={googleMyBusinessConditionRef}
          tiktokConditionRef={tiktokConditionRef}
          linkedInConditionRef={linkedInConditionRef}
          youtubeConditionRef={youtubeConditionRef}
          twitterConditionRef={twitterConditionRef}
          threadsConditionRef={threadsConditionRef}
          instagramConditionRef={instagramConditionRef}
          facebookConditionRef={facebookConditionRef}
          pinterestConditionRef={pinterestConditionRef}
          blueskyConditionRef={blueskyConditionRef}
          snapchatConditionRef={snapchatConditionRef}
          previewRef={previewRef}
          handleSaveDraft={handleSaveDraft}
          isSubmittingDraft={isSubmittingDraft}
          handleScheduleDate={handleScheduleDate}
          scheduleDateTime={scheduleDateTime}
          whenToPost={whenToPost}
          PostOption={PostOption}
          handlePostTypeChange={handlePostTypeChange}
          isSubmitting={isSubmitting}
          handleFormsubmit={handleFormsubmit}
          hasApproverPermission={hasApproverPermission}
          handleSelectPostTypeChange={handleSelectPostTypeChange}
        />
      ) : (
        <Grid container spacing={2}>
          <Grid item xs={12} md={6} sm={12} style={{ paddingBottom: '12px' }}>
            <PostContentComponent
              configuredSocialMedia={configuredSocialMedia}
              handleProfileClick={handleProfileClick}
              selectedSocialMedias={selectedSocialMedias}
              handleNetworks={handleNetworks}
              newPostForm={newPostForm}
              handleAddAllDescription={handleAddAllDescription}
              handleAddAllCommentDescription={handleAddAllCommentDescription}
              handleMediaFiles={handleMediaFiles}
              hashTags={hashTags}
              handleAddHashTags={handleAddHashTags}
              isOrgConnectedProfilesData={false}
              isShowOfferBanner={isShowOfferBanner}
            />
          </Grid>
          <Grid item xs={12} md={6} style={{ paddingBottom: '12px' }}>
            <PostTabComponent
              tabValue={tabValue}
              setTabValue={setTabValue}
              updateRequestData={updateRequestData}
              googleMyBusinessConditionRef={googleMyBusinessConditionRef}
              tiktokConditionRef={tiktokConditionRef}
              linkedInConditionRef={linkedInConditionRef}
              youtubeConditionRef={youtubeConditionRef}
              twitterConditionRef={twitterConditionRef}
              threadsConditionRef={threadsConditionRef}
              instagramConditionRef={instagramConditionRef}
              facebookConditionRef={facebookConditionRef}
              pinterestConditionRef={pinterestConditionRef}
              blueskyConditionRef={blueskyConditionRef}
              snapchatConditionRef={snapchatConditionRef}
              previewRef={previewRef}
              newPostForm={newPostForm}
              isShowOfferBanner={isShowOfferBanner}
            />
          </Grid>
          <PostFooter
            handleSaveDraft={handleSaveDraft}
            isSubmittingDraft={isSubmittingDraft}
            handleScheduleDate={handleScheduleDate}
            scheduleDateTime={scheduleDateTime}
            whenToPost={whenToPost}
            PostOption={PostOption}
            hasApproverPermission={hasApproverPermission}
            handlePostTypeChange={handlePostTypeChange}
            handleSelectPostTypeChange={handleSelectPostTypeChange}
            isSubmitting={isSubmitting}
            handleFormsubmit={handleFormsubmit}
          />
        </Grid>
      )}
      {reAuthenticateOpen && (
        <CommonReAuthenticateComponent
          selectedPopOverAccount={selectedPopOverAccount}
          getConnectedNetworks={getConnectedNetworks}
          onClose={handleCloseReauthenticate}
        />
      )}
    </>
  );
};
export default NewPostComponent;
